import React, { useState, useEffect, useRef } from 'react';
import './AdvancedFormBuilder.css';
import { DEFAULT_FORM, DEFAULT_GLOBAL_STYLES, createBaseElement } from './defaults';
import ElementPreview from './renderers/ElementPreview';
import { DesignPropertiesPanel } from './DesignPropertiesPanel';
import { SectionPropertiesPanel } from './SectionPropertiesPanel';
import { FormBuilderCtx } from './FormBuilderContext';
import { FormBuilderToolbar } from './FormBuilderToolbar';
import { FieldPalettePanel } from './FieldPalettePanel';
import { FormCanvasArea } from './FormCanvasArea';
import { FormPropertiesPanel } from './FormPropertiesPanel';

const AdvancedFormBuilder = ({ onFormCreated, initialForm }) => {
  // Initial defaults extracted to ./defaults.js (was a 30-line inline literal here).
  const defaultForm = DEFAULT_FORM;

  const [sections, setSections] = useState(defaultForm);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedSection, setSelectedSection] = useState('section-1');
  const [globalStyles, setGlobalStyles] = useState(DEFAULT_GLOBAL_STYLES);

  // Drag and drop state
  const [draggedElement, setDraggedElement] = useState(null);
  const [dragOverElement, setDragOverElement] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragSource, setDragSource] = useState(null);

  // Sidebar and Properties collapse state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [propertiesCollapsed, setPropertiesCollapsed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState('elements');
  const [propertiesTab, setPropertiesTab] = useState('content');

  // State for global elements
  const [globalElements, setGlobalElements] = useState([]);

  // Resize state
  const [resizing, setResizing] = useState(false);
  const [resizeData, setResizeData] = useState({
    elementId: null,
    direction: '',
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0
  });

  // Refs
  const fileInputRef = useRef(null);
  const backgroundImageInputRef = useRef(null);
  const elementBackgroundInputRef = useRef(null);
  const fullscreenRef = useRef(null);
  const dropZoneRef = useRef(null);
  const previewRef = useRef(null);

  // Load initial form
  useEffect(() => {
    if (initialForm) {
      setSections(
        initialForm.sections?.map(section => ({
          ...section,
          titleStyling: section.titleStyling || {
            textAlign: 'left',
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#2d3748',
            marginBottom: '15px'
          },
          styling: {
            ...section.styling,
            alignSelf: section.styling?.alignSelf || 'center',
            marginLeft: section.styling?.marginLeft || 'auto',
            marginRight: section.styling?.marginRight || 'auto'
          }
        })) || DEFAULT_FORM
      );
      setGlobalStyles(initialForm.globalStyles || DEFAULT_GLOBAL_STYLES);
      setGlobalElements(initialForm.globalElements || []);
    }
  }, [initialForm]);

  // Fullscreen functionality
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      fullscreenRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Resize handlers
  const handleResizeStart = (e, elementId, direction, isGlobal = false) => {
    e.preventDefault();
    e.stopPropagation();

    const element = isGlobal
      ? globalElements.find(el => el.id === elementId)
      : sections.flatMap(s => s.elements).find(el => el.id === elementId);

    if (!element) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const currentWidth = element.styling.width || '100%';
    const currentHeight = element.styling.height || 'auto';

    const startWidth = currentWidth.includes('px') ? parseInt(currentWidth) :
      currentWidth.includes('%') ? parseInt(currentWidth) * (previewRef.current?.offsetWidth || 800) / 100 : 100;
    const startHeight = currentHeight.includes('px') ? parseInt(currentHeight) : 0;

    setResizing(true);
    setResizeData({
      elementId,
      direction,
      startX,
      startY,
      startWidth,
      startHeight,
      isGlobal
    });

    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResize = (e) => {
    if (!resizing) return;

    const { elementId, direction, startX, startY, startWidth, startHeight, isGlobal } = resizeData;
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;

    let newWidth = startWidth;
    let newHeight = startHeight;

    switch (direction) {
      case 'right':
        newWidth = Math.max(50, startWidth + deltaX);
        break;
      case 'left':
        newWidth = Math.max(50, startWidth - deltaX);
        break;
      case 'bottom':
        newHeight = Math.max(20, startHeight + deltaY);
        break;
      case 'top':
        newHeight = Math.max(20, startHeight - deltaY);
        break;
      case 'bottom-right':
        newWidth = Math.max(50, startWidth + deltaX);
        newHeight = Math.max(20, startHeight + deltaY);
        break;
      case 'bottom-left':
        newWidth = Math.max(50, startWidth - deltaX);
        newHeight = Math.max(20, startHeight + deltaY);
        break;
      case 'top-right':
        newWidth = Math.max(50, startWidth + deltaX);
        newHeight = Math.max(20, startHeight - deltaY);
        break;
      case 'top-left':
        newWidth = Math.max(50, startWidth - deltaX);
        newHeight = Math.max(20, startHeight - deltaY);
        break;
      default:
        break;
    }

    // Update element styling
    const styleUpdates = { width: `${newWidth}px` };
    if (newHeight > 0) {
      styleUpdates.height = `${newHeight}px`;
    }

    updateElementStyle(elementId, styleUpdates, isGlobal);
  };

  const handleResizeEnd = () => {
    setResizing(false);
    setResizeData({
      elementId: null,
      direction: '',
      startX: 0,
      startY: 0,
      startWidth: 0,
      startHeight: 0
    });

    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  // Add resize handles to form elements
  const renderResizeHandles = (elementId, isGlobal = false) => {
    if (!selectedElement || selectedElement !== elementId) return null;

    return (
      <>
        <div
          className="resize-handle top-left"
          onMouseDown={(e) => handleResizeStart(e, elementId, 'top-left', isGlobal)}
        />
        <div
          className="resize-handle top-right"
          onMouseDown={(e) => handleResizeStart(e, elementId, 'top-right', isGlobal)}
        />
        <div
          className="resize-handle bottom-left"
          onMouseDown={(e) => handleResizeStart(e, elementId, 'bottom-left', isGlobal)}
        />
        <div
          className="resize-handle bottom-right"
          onMouseDown={(e) => handleResizeStart(e, elementId, 'bottom-right', isGlobal)}
        />
        <div
          className="resize-handle right"
          onMouseDown={(e) => handleResizeStart(e, elementId, 'right', isGlobal)}
        />
        <div
          className="resize-handle left"
          onMouseDown={(e) => handleResizeStart(e, elementId, 'left', isGlobal)}
        />
        <div
          className="resize-handle top"
          onMouseDown={(e) => handleResizeStart(e, elementId, 'top', isGlobal)}
        />
        <div
          className="resize-handle bottom"
          onMouseDown={(e) => handleResizeStart(e, elementId, 'bottom', isGlobal)}
        />
      </>
    );
  };

  // Handle background image upload
  const handleBackgroundImageUpload = (event, target = 'global') => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target.result;
        if (target === 'global') {
          setGlobalStyles(prev => ({
            ...prev,
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }));
        } else if (target === 'section' && selectedSection) {
          updateSectionStyle(selectedSection, {
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          });
        }
      };
      reader.readAsDataURL(file);
    }
    // Reset the input
    event.target.value = '';
  };

  // Section background image handling
  const handleSectionBackgroundUpload = (event, sectionId) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target.result;
        updateSectionStyle(sectionId, {
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        });
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  // Remove background image
  const removeBackgroundImage = (target = 'global') => {
    if (target === 'global') {
      setGlobalStyles(prev => ({
        ...prev,
        backgroundImage: '',
        backgroundSize: '',
        backgroundPosition: '',
        backgroundRepeat: ''
      }));
    } else if (target === 'section' && selectedSection) {
      updateSectionStyle(selectedSection, {
        backgroundImage: '',
        backgroundSize: '',
        backgroundPosition: '',
        backgroundRepeat: ''
      });
    }
  };

  // Remove section background image
  const removeSectionBackgroundImage = (sectionId) => {
    updateSectionStyle(sectionId, {
      backgroundImage: '',
      backgroundSize: '',
      backgroundPosition: '',
      backgroundRepeat: ''
    });
  };

  // Handle element background image upload
  const handleElementBackgroundUpload = (elementId, file, isGlobal = false) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        updateElementStyle(elementId, {
          backgroundImage: `url(${e.target.result})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }, isGlobal);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove element background image
  const removeElementBackgroundImage = (elementId, isGlobal = false) => {
    updateElementStyle(elementId, {
      backgroundImage: '',
      backgroundSize: '',
      backgroundPosition: '',
      backgroundRepeat: ''
    }, isGlobal);
  };

  // Expanded element types inspired by GrapesJS
  const elementTypes = [
    // Basic Inputs
    { type: 'text', label: 'Text Input', icon: '📝', category: 'basic' },
    { type: 'email', label: 'Email Input', icon: '📧', category: 'basic' },
    { type: 'number', label: 'Number Input', icon: '🔢', category: 'basic' },
    { type: 'textarea', label: 'Text Area', icon: '📄', category: 'basic' },
    { type: 'select', label: 'Dropdown', icon: '📋', category: 'basic' },
    { type: 'checkbox', label: 'Checkbox', icon: '☑️', category: 'basic' },
    { type: 'radio', label: 'Radio Group', icon: '🔘', category: 'basic' },
    // Advanced Inputs
    { type: 'date', label: 'Date Picker', icon: '📅', category: 'advanced' },
    { type: 'file', label: 'File Upload', icon: '📎', category: 'advanced' },
    { type: 'range', label: 'Range Slider', icon: '🎚️', category: 'advanced' },
    { type: 'color', label: 'Color Picker', icon: '🎨', category: 'advanced' },
    { type: 'tel', label: 'Phone Input', icon: '📞', category: 'advanced' },
    { type: 'url', label: 'URL Input', icon: '🔗', category: 'advanced' },
    { type: 'password', label: 'Password', icon: '🔒', category: 'advanced' },
    // Layout Elements
    { type: 'header', label: 'Header', icon: '🏷️', category: 'layout' },
    { type: 'divider', label: 'Divider', icon: '➖', category: 'layout' },
    { type: 'image', label: 'Image', icon: '🖼️', category: 'layout' },
    { type: 'video', label: 'Video', icon: '🎥', category: 'layout' },
    { type: 'map', label: 'Map', icon: '🗺️', category: 'layout' },
    { type: 'table', label: 'Table', icon: '📊', category: 'layout' },
    { type: 'link', label: 'Link', icon: '🔗', category: 'layout' },
    { type: 'container', label: 'Container', icon: '📍', category: 'layout' },
    // Action Elements
    { type: 'button', label: 'Button', icon: '🔄', category: 'action' },
    { type: 'submit', label: 'Submit', icon: '✅', category: 'action' },
  ];

  const hasExistingForm = () => {
    return sections.some(section => section.elements.length > 0) || globalElements.length > 0;
  };

  // Section Management
  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      title: `Section ${sections.length + 1}`,
      titleStyling: {
        textAlign: 'left',
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#2d3748',
        marginBottom: '15px'
      },
      columns: 1,
      elements: [],
      styling: {
        backgroundColor: '#ffffff',
        padding: '30px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        margin: '20px 0',
        alignSelf: 'center',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
        maxWidth: '800px'
      }
    };
    setSections(prev => [...prev, newSection]);
    setSelectedSection(newSection.id);
  };

  const removeSection = (sectionId) => {
    if (sections.length > 1) {
      setSections(prev => prev.filter(section => section.id !== sectionId));
      if (selectedSection === sectionId) {
        setSelectedSection(sections[0].id);
      }
    }
  };

  const updateSection = (sectionId, updates) => {
    setSections(prev =>
      prev.map(section =>
        section.id === sectionId ? { ...section, ...updates } : section
      )
    );
  };

  const updateSectionStyle = (sectionId, styleUpdates) => {
    setSections(prev =>
      prev.map(section =>
        section.id === sectionId
          ? { ...section, styling: { ...section.styling, ...styleUpdates } }
          : section
      )
    );
  };

  const updateSectionTitleStyle = (sectionId, styleUpdates) => {
    setSections(prev =>
      prev.map(section =>
        section.id === sectionId
          ? { ...section, titleStyling: { ...section.titleStyling, ...styleUpdates } }
          : section
      )
    );
  };


  // Element Management
  const addFormElement = (type, target = 'section') => {
    const baseElement = createBaseElement(type);

    if (target === 'global') {
      setGlobalElements(prev => [...prev, baseElement]);
    } else {
      setSections(prev =>
        prev.map(section =>
          section.id === selectedSection
            ? { ...section, elements: [...section.elements, baseElement] }
            : section
        )
      );
    }
  };

  const addGlobalElement = (type) => {
    addFormElement(type, 'global');
  };

  const updateElement = (elementId, updates, isGlobal = false) => {
    if (isGlobal) {
      setGlobalElements(prev =>
        prev.map(el =>
          el.id === elementId ? { ...el, ...updates } : el
        )
      );
    } else {
      setSections(prev =>
        prev.map(section => ({
          ...section,
          elements: section.elements.map(el =>
            el.id === elementId ? { ...el, ...updates } : el
          )
        }))
      );
    }
  };

  const updateElementStyle = (elementId, styleUpdates, isGlobal = false) => {
    if (isGlobal) {
      setGlobalElements(prev =>
        prev.map(el =>
          el.id === elementId
            ? { ...el, styling: { ...el.styling, ...styleUpdates } }
            : el
        )
      );
    } else {
      setSections(prev =>
        prev.map(section => ({
          ...section,
          elements: section.elements.map(el =>
            el.id === elementId
              ? { ...el, styling: { ...el.styling, ...styleUpdates } }
              : el
          )
        }))
      );
    }
  };

  const updateLabelStyle = (elementId, styleUpdates, isGlobal = false) => {
    if (isGlobal) {
      setGlobalElements(prev =>
        prev.map(el =>
          el.id === elementId
            ? { ...el, labelStyle: { ...el.labelStyle, ...styleUpdates } }
            : el
        )
      );
    } else {
      setSections(prev =>
        prev.map(section => ({
          ...section,
          elements: section.elements.map(el =>
            el.id === elementId
              ? { ...el, labelStyle: { ...el.labelStyle, ...styleUpdates } }
              : el
          )
        }))
      );
    }
  };

  const updateElementAlignment = (elementId, alignment, textAlign, isGlobal = false) => {
    if (isGlobal) {
      setGlobalElements(prev =>
        prev.map(el =>
          el.id === elementId
            ? {
              ...el,
              alignment,
              textAlign,
              styling: {
                ...el.styling,
                textAlign: textAlign,
                marginLeft: alignment === 'center' ? 'auto' : alignment === 'right' ? 'auto' : '0',
                marginRight: alignment === 'center' ? 'auto' : alignment === 'right' ? '0' : 'auto'
              }
            }
            : el
        )
      );
    } else {
      setSections(prev =>
        prev.map(section => ({
          ...section,
          elements: section.elements.map(el =>
            el.id === elementId
              ? {
                ...el,
                alignment,
                textAlign,
                styling: {
                  ...el.styling,
                  textAlign: textAlign,
                  marginLeft: alignment === 'center' ? 'auto' : alignment === 'right' ? 'auto' : '0',
                  marginRight: alignment === 'center' ? 'auto' : alignment === 'right' ? '0' : 'auto'
                }
              }
              : el
          )
        }))
      );
    }
  };

  const removeElement = (elementId, isGlobal = false) => {
    if (isGlobal) {
      setGlobalElements(prev => prev.filter(el => el.id !== elementId));
    } else {
      setSections(prev =>
        prev.map(section => ({
          ...section,
          elements: section.elements.filter(el => el.id !== elementId)
        }))
      );
    }

    if (selectedElement === elementId) {
      setSelectedElement(null);
    }
  };

  const moveElement = (sectionId, fromIndex, toIndex) => {
    setSections(prev =>
      prev.map(section => {
        if (section.id === sectionId) {
          const newElements = [...section.elements];
          const [movedElement] = newElements.splice(fromIndex, 1);
          newElements.splice(toIndex, 0, movedElement);
          return { ...section, elements: newElements };
        }
        return section;
      })
    );
  };

  const moveGlobalElement = (fromIndex, toIndex) => {
    setGlobalElements(prev => {
      const newElements = [...prev];
      const [movedElement] = newElements.splice(fromIndex, 1);
      newElements.splice(toIndex, 0, movedElement);
      return newElements;
    });
  };

  // Drag and Drop Functions
  const handleDragStart = (e, elementType, source = 'sidebar', isGlobal = false) => {
    e.dataTransfer.setData('elementType', elementType);
    e.dataTransfer.setData('source', source);
    e.dataTransfer.setData('isGlobal', isGlobal.toString());
    setDragSource(source);

    if (source === 'preview') {
      const elementId = e.target.closest('.form-element').dataset.id;
      setDraggedElement(elementId);
      setIsDragging(true);
      e.target.classList.add('dragging');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();

    if (dragSource === 'preview') {
      const element = e.target.closest('.form-element');
      if (element) {
        setDragOverElement(element.dataset.id);
        element.classList.add('drag-over');
      }
    }
  };

  const handleDragLeave = (e) => {
    if (dragSource === 'preview') {
      const element = e.target.closest('.form-element');
      if (element) {
        element.classList.remove('drag-over');
      }
    }
  };

  const handleDrop = (e, targetSectionId = null, targetIndex = null, isGlobalDrop = false) => {
    e.preventDefault();

    const elementType = e.dataTransfer.getData('elementType');
    const source = e.dataTransfer.getData('source');
    const isGlobal = e.dataTransfer.getData('isGlobal') === 'true';

    if (source === 'sidebar') {
      if (isGlobalDrop) {
        addGlobalElement(elementType);
      } else {
        addFormElement(elementType);
      }
    } else if (source === 'preview') {
      const sourceElementId = draggedElement;
      const targetElementId = dragOverElement;

      if (sourceElementId && targetElementId && sourceElementId !== targetElementId) {
        if (isGlobal) {
          const sourceIndex = globalElements.findIndex(el => el.id === sourceElementId);
          const targetIndex = globalElements.findIndex(el => el.id === targetElementId);

          if (sourceIndex !== -1 && targetIndex !== -1) {
            moveGlobalElement(sourceIndex, targetIndex);
          }
        } else {
          const currentSection = sections.find(s => s.id === selectedSection);
          const sourceIndex = currentSection.elements.findIndex(el => el.id === sourceElementId);
          const targetIndex = currentSection.elements.findIndex(el => el.id === targetElementId);

          if (sourceIndex !== -1 && targetIndex !== -1) {
            moveElement(selectedSection, sourceIndex, targetIndex);
          }
        }
      }
    }

    setIsDragging(false);
    setDraggedElement(null);
    setDragOverElement(null);
    setDragSource(null);

    document.querySelectorAll('.form-element').forEach(el => {
      el.classList.remove('dragging', 'drag-over');
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedElement(null);
    setDragOverElement(null);
    setDragSource(null);

    document.querySelectorAll('.form-element').forEach(el => {
      el.classList.remove('dragging', 'drag-over');
    });
  };

  const handleImageUpload = (elementId, file, isGlobal = false) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        updateElement(elementId, { imageUrl: e.target.result }, isGlobal);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUpload = (elementId, file, isGlobal = false) => {
    if (file && file.type.startsWith('video/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        updateElement(elementId, { videoUrl: e.target.result }, isGlobal);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        const newImageElement = createBaseElement('image');
        newImageElement.imageUrl = URL.createObjectURL(file);
        newImageElement.altText = file.name;
        setGlobalElements(prev => [...prev, newImageElement]);
      } else if (file.type.startsWith('video/')) {
        const newVideoElement = createBaseElement('video');
        newVideoElement.videoUrl = URL.createObjectURL(file);
        setGlobalElements(prev => [...prev, newVideoElement]);
      }
    }
  };

  // Style Presets Function
  const applyStylePreset = (preset, elementId, isGlobal) => {
    const presets = {
      primary: {
        backgroundColor: '#007bff',
        color: '#ffffff',
        borderColor: '#007bff',
        borderRadius: '6px',
        padding: '10px 20px',
        fontWeight: '600'
      },
      secondary: {
        backgroundColor: '#6c757d',
        color: '#ffffff',
        borderColor: '#6c757d',
        borderRadius: '6px',
        padding: '10px 20px',
        fontWeight: '600'
      },
      success: {
        backgroundColor: '#28a745',
        color: '#ffffff',
        borderColor: '#28a745',
        borderRadius: '6px',
        padding: '10px 20px',
        fontWeight: '600'
      },
      danger: {
        backgroundColor: '#dc3545',
        color: '#ffffff',
        borderColor: '#dc3545',
        borderRadius: '6px',
        padding: '10px 20px',
        fontWeight: '600'
      },
      warning: {
        backgroundColor: '#ffc107',
        color: '#212529',
        borderColor: '#ffc107',
        borderRadius: '6px',
        padding: '10px 20px',
        fontWeight: '600'
      },
      info: {
        backgroundColor: '#17a2b8',
        color: '#ffffff',
        borderColor: '#17a2b8',
        borderRadius: '6px',
        padding: '10px 20px',
        fontWeight: '600'
      }
    };

    updateElementStyle(elementId, presets[preset], isGlobal);
  };

  // Enhanced Design Properties
  // Delegated to DesignPropertiesPanel.jsx (see archive/AdvancedFormBuilder.v1.full.js for original 746-line body)
  const renderDesignProperties = (selectedElementData, isSelectedElementGlobal) => (
    <DesignPropertiesPanel
      selectedElementData={selectedElementData}
      isGlobal={isSelectedElementGlobal}
      onUpdateAlignment={updateElementAlignment}
      onUpdateStyle={updateElementStyle}
      onElementBackgroundUpload={handleElementBackgroundUpload}
      onRemoveElementBackground={removeElementBackgroundImage}
      onApplyPreset={applyStylePreset}
    />
  );
  // Enhanced section properties rendering

  // Delegated to SectionPropertiesPanel.jsx (see archive/AdvancedFormBuilder.v1.full.js for original 424-line body)
  const renderSectionProperties = () => (
    <SectionPropertiesPanel
      currentSection={currentSection}
      onUpdateSection={updateSection}
      onUpdateSectionStyle={updateSectionStyle}
      onUpdateSectionTitleStyle={updateSectionTitleStyle}
      onSectionBackgroundUpload={handleSectionBackgroundUpload}
    />
  );

  const generateForm = () => {
    const formConfig = {
      title: 'Generated Form',
      sections: sections.map(section => ({
        ...section,
        titleStyling: section.titleStyling
      })),
      globalStyles: globalStyles,
      globalElements: globalElements,
      createdAt: new Date().toISOString(),
      totalElements: sections.reduce((total, section) => total + section.elements.length, 0) + globalElements.length
    };
    onFormCreated(formConfig);
  };


  const currentSection = sections.find(s => s.id === selectedSection);
  const selectedElementData = selectedElement ?
    [...globalElements, ...sections.flatMap(section => section.elements)]
      .find(el => el.id === selectedElement) : null;

  const isSelectedElementGlobal = selectedElement ?
    globalElements.some(el => el.id === selectedElement) : false;

  return (
    <FormBuilderCtx.Provider value={{
      // State
      sections, setSections,
      selectedElement, setSelectedElement,
      selectedSection, setSelectedSection,
      globalStyles, setGlobalStyles,
      isPreviewMode: false, setIsPreviewMode: () => {},
      draggedElement, setDraggedElement,
      dragOverElement, setDragOverElement,
      isDragging, setIsDragging,
      dragSource, setDragSource,
      sidebarCollapsed, setSidebarCollapsed,
      propertiesCollapsed, setPropertiesCollapsed,
      isFullscreen, setIsFullscreen,
      activeTab, setActiveTab,
      propertiesTab, setPropertiesTab,
      globalElements, setGlobalElements,
      resizing, setResizing,
      resizeData, setResizeData,
      // Refs
      fileInputRef, backgroundImageInputRef, elementBackgroundInputRef,
      fullscreenRef, dropZoneRef, previewRef,
      // Handlers
      toggleFullscreen, hasExistingForm, generateForm,
      handleResizeStart, handleResize, handleResizeEnd, renderResizeHandles,
      handleBackgroundImageUpload, handleSectionBackgroundUpload,
      removeBackgroundImage, removeSectionBackgroundImage,
      handleElementBackgroundUpload, removeElementBackgroundImage,
      addSection, removeSection, updateSection, updateSectionStyle, updateSectionTitleStyle,
      addFormElement, addGlobalElement,
      updateElement, updateElementStyle, updateLabelStyle, updateElementAlignment,
      removeElement, moveElement, moveGlobalElement,
      handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
      handleImageUpload, handleVideoUpload, handleFileDrop,
      applyStylePreset,
      // Derived / render helpers
      elementTypes, currentSection, selectedElementData, isSelectedElementGlobal,
      renderDesignProperties, renderSectionProperties,
    }}>
      <div className={`advanced-form-builder ${isFullscreen ? 'fullscreen' : ''}`} ref={fullscreenRef}>
        <FormBuilderToolbar />
        <div className="builder-layout">
          <FieldPalettePanel />
          <FormCanvasArea />
          <FormPropertiesPanel />
        </div>
      </div>
    </FormBuilderCtx.Provider>
  );
};

export default AdvancedFormBuilder;
